import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { extractError } from '../../api/client';

/**
 * Factory for a category CRUD slice (bhajans / publications / gallery).
 * `api` must expose { list, create, update, remove }.
 */
export const createCategorySlice = ({ name, api }) => {
  const fetchCategories = createAsyncThunk(`${name}/fetchCategories`, async (_, { rejectWithValue }) => {
    try {
      const data = await api.list();
      return data.results ?? data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  });

  const createCategory = createAsyncThunk(`${name}/createCategory`, async (payload, { rejectWithValue }) => {
    try {
      return await api.create(payload);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  });

  const updateCategory = createAsyncThunk(`${name}/updateCategory`, async ({ slug, payload }, { rejectWithValue }) => {
    try {
      return await api.update(slug, payload);
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  });

  const deleteCategory = createAsyncThunk(`${name}/deleteCategory`, async (slug, { rejectWithValue }) => {
    try {
      await api.remove(slug);
      return slug;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  });

  const slice = createSlice({
    name,
    initialState: {
      items: [],
      status: 'idle',
      error: null,
    },
    reducers: {
      clearCategoryError(state) {
        state.error = null;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchCategories.pending, (state) => {
          state.status = 'loading';
        })
        .addCase(fetchCategories.fulfilled, (state, action) => {
          state.status = 'succeeded';
          state.items = action.payload;
        })
        .addCase(fetchCategories.rejected, (state, action) => {
          state.status = 'failed';
          state.error = action.payload?.message || 'Failed to load categories';
        })
        .addCase(createCategory.fulfilled, (state, action) => {
          state.items = [...state.items, action.payload];
        })
        .addCase(createCategory.rejected, (state, action) => {
          state.error = action.payload?.message || 'Failed to create category';
        })
        .addCase(updateCategory.fulfilled, (state, action) => {
          state.items = state.items.map((c) => (c.slug === action.payload.slug ? action.payload : c));
        })
        .addCase(updateCategory.rejected, (state, action) => {
          state.error = action.payload?.message || 'Failed to update category';
        })
        .addCase(deleteCategory.pending, (state, action) => {
          // Optimistic removal so the row disappears instantly.
          state.items = state.items.filter((c) => c.slug !== action.meta.arg);
        })
        .addCase(deleteCategory.fulfilled, (state, action) => {
          state.items = state.items.filter((c) => c.slug !== action.payload);
          state.error = null;
        })
        .addCase(deleteCategory.rejected, (state, action) => {
          // The component refetches the list to restore truth, so do not
          // surface a duplicate raw error via the global banner here.
          state.error = null;
        });
    },
  });

  return {
    slice,
    actions: {
      ...slice.actions,
      fetchCategories,
      createCategory,
      updateCategory,
      deleteCategory,
    },
  };
};
